import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";

import { adminDeclineFormSchema, AdminDeclineFormSchema } from "./schema";
import { useSetCandidateOpportunityStatus } from "@/mutations/admin/set-candidate-opportunity-status";

import { candidateDeclineReasonList } from "@/types";
import { AdminJobCandidateDetails } from "@/queries/admin/job-details";

enum MODE {
  REJECT = "REJECT",
  OPTOUT = "OPTOUT",
}

export const AdminDeclineOpportunityForm = ({
  openingId,
  candidateStatusId,
  onCancel,
  onSuccess,
}: {
  openingId: string;
  candidateStatusId: string;
  onCancel: () => void;
  onSuccess?: (data: AdminJobCandidateDetails) => void;
}) => {
  const [mode, setMode] = useState<MODE | undefined>();

  const form = useForm<AdminDeclineFormSchema>({
    resolver: zodResolver(adminDeclineFormSchema),
    mode: "onChange",
    defaultValues: {
      declineReasons: [],
      otherDeclineReason: "",
    },
  });

  const { setCandidateOpportunityStatus, isSettingCandidateOpportunityStatus } =
    useSetCandidateOpportunityStatus({ openingId, onSuccess });

  function onRejectSubmit() {
    if (isSettingCandidateOpportunityStatus) return;

    setCandidateOpportunityStatus({
      statusId: candidateStatusId,
      status: "Rejected for Role",
    });
  }

  function onOptOutSubmit(data: AdminDeclineFormSchema) {
    if (isSettingCandidateOpportunityStatus) return;

    const reasons: string[] = [...data.declineReasons];
    if (data.otherDeclineReason) {
      reasons.push(data.otherDeclineReason);
    }

    setCandidateOpportunityStatus({
      statusId: candidateStatusId,
      status: "Not Interested",
      optOutReasons: reasons,
    });
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label className="text-base text-[#2E2E38] font-normal" required>
          Decline type
        </Label>
        <RadioGroup
          value={mode}
          onValueChange={(data) => setMode(data as MODE)}
        >
          <RadioGroupItem
            variant="radioBar"
            label="Reject candidate"
            value={MODE.REJECT}
          />
          <RadioGroupItem
            variant="radioBar"
            label="Opt-out on candidate's behalf"
            value={MODE.OPTOUT}
          />
        </RadioGroup>
      </div>
      {mode === MODE.OPTOUT && (
        <Form {...form}>
          <form
            className="w-full flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onOptOutSubmit)}
          >
            <FormField
              control={form.control}
              name="declineReasons"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reasons</FormLabel>
                  <FormControl>
                    <MultiSelect
                      values={field.value}
                      setValues={field.onChange}
                      options={candidateDeclineReasonList}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="otherDeclineReason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Reason(s)</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      )}
      <div className="h-[1px] bg-[#E4E7EC] w-full" />
      <div className="!mb-1 flex justify-between">
        <Button variant="ghost" onClick={onCancel}>
          Back
        </Button>
        <Button
          disabled={!mode || isSettingCandidateOpportunityStatus}
          variant="secondary"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={() =>
            mode === MODE.REJECT
              ? onRejectSubmit()
              : form.handleSubmit(onOptOutSubmit)()
          }
        >
          {isSettingCandidateOpportunityStatus && (
            <Loader2 className="animate-spin" />
          )}
          Decline Candidate
        </Button>
      </div>
    </div>
  );
};
